import { any, when, propEq, curry, assoc, prop } from 'ramda';
import type { Reducer } from 'redux';
import type { Locale } from '@peloton/internationalize';
import type { ClassLanguage, Caption } from '@engage/settings';

// Types

export type ClassLanguageState = ClassLanguage[];

export type ClassLanguageAction =
  | ReturnType<typeof displayAllClasses>
  | ReturnType<typeof displayOnlySubtitles>
  | ReturnType<typeof toggleIndividualSubtitle>
  | ReturnType<typeof disableLanguage>
  | ReturnType<typeof enableLanguage>
  | ReturnType<typeof replaceState>;

export enum ClassLanguageActionTypes {
  DisplayAllClasses = 'members/settings/classLanguages/DISPLAY_ALL',
  DisplayOnlySubtitles = 'members/settings/classLanguages/DISPLAY_ONLY_SUBTITLES',
  ToggleIndividualSubtitle = 'members/settings/classLanguages/TOGGLE_SUBTITLE',
  DisableLanguage = 'members/settings/classLanguages/DISABLE',
  EnableLanguage = 'members/settings/classLanguages/ENABLE',
  ReplaceState = 'members/settings/classLanguages/REPLACE',
}

// Reducer

export const classLanguagesReducer: Reducer<ClassLanguageState, ClassLanguageAction> = (
  state = [],
  action,
) => {
  switch (action.type) {
    case ClassLanguageActionTypes.DisplayAllClasses:
      return updateLanguageByLocale(
        state,
        action.payload.languageLocale,
        curry(enableOrDisableDisplayAll)(true),
      );
    case ClassLanguageActionTypes.DisplayOnlySubtitles:
      return updateLanguageByLocale(
        state,
        action.payload.languageLocale,
        curry(enableOrDisableDisplayAll)(false),
      );
    case ClassLanguageActionTypes.ToggleIndividualSubtitle:
      return updateLanguageByLocale(
        state,
        action.payload.languageLocale,
        curry(updateIndividualSubtitle)(action.payload.subtitleLocale),
      );
    case ClassLanguageActionTypes.DisableLanguage:
      return updateLanguageByLocale(state, action.payload.locale, disableLanguageState);
    case ClassLanguageActionTypes.EnableLanguage:
      return updateLanguageByLocale(
        state,
        action.payload.locale,
        assoc('displayAllClasses', true),
      );
    case ClassLanguageActionTypes.ReplaceState:
      return action.payload;
    default:
      return state;
  }
};

// State Transformers

const enableOrDisableDisplayAll = (
  displayAll: boolean,
  language: ClassLanguage,
): ClassLanguage => ({
  ...language,
  displayAllClasses: displayAll,
  captions: enableOrDisableAllCaptions(language.captions, !displayAll),
});

const updateIndividualSubtitle = (
  subtitleLocale: Locale,
  language: ClassLanguage,
): ClassLanguage => {
  const captions = toggleSingleCaption(language.captions, subtitleLocale);
  return {
    ...language,
    captions,
    displayAllClasses: !areAnyCaptionsEnabled(captions),
  };
};

const disableLanguageState = (language: ClassLanguage): ClassLanguage => ({
  ...language,
  displayAllClasses: false,
  captions: enableOrDisableAllCaptions(language.captions, false),
});

const updateLanguageByLocale = (
  state: ClassLanguageState,
  locale: Locale,
  updater: (language: ClassLanguage) => ClassLanguage,
) => state.map(when<ClassLanguage, ClassLanguage>(propEq('locale', locale), updater));

const enableOrDisableAllCaptions = (captions: Caption[], enabled: boolean) =>
  captions.map(caption => ({ ...caption, enabled }));

const toggleSingleCaption = (captions: Caption[], locale: Locale) =>
  captions.map(caption =>
    caption.locale === locale ? { ...caption, enabled: !caption.enabled } : caption,
  );

// Helpers

export const areAnyCaptionsEnabled = (captions: Caption[]) =>
  any(prop('enabled'), captions);

export const isLanguageEnabled = (language: ClassLanguage) =>
  language.displayAllClasses || areAnyCaptionsEnabled(language.captions);

export const isValid = (classLanguages: ClassLanguageState) =>
  any(isLanguageEnabled, classLanguages);

// Actions

export const displayOnlySubtitles = (languageLocale: Locale) =>
  ({
    type: ClassLanguageActionTypes.DisplayOnlySubtitles,
    payload: { languageLocale },
  } as const);

export const displayAllClasses = (languageLocale: Locale) =>
  ({
    type: ClassLanguageActionTypes.DisplayAllClasses,
    payload: { languageLocale },
  } as const);

export const toggleIndividualSubtitle = ({
  languageLocale,
  subtitleLocale,
}: {
  languageLocale: Locale;
  subtitleLocale: Locale;
}) =>
  ({
    type: ClassLanguageActionTypes.ToggleIndividualSubtitle,
    payload: { languageLocale, subtitleLocale },
  } as const);

export const disableLanguage = (locale: Locale) =>
  ({
    type: ClassLanguageActionTypes.DisableLanguage,
    payload: { locale },
  } as const);

export const enableLanguage = (locale: Locale) =>
  ({
    type: ClassLanguageActionTypes.EnableLanguage,
    payload: { locale },
  } as const);

export const replaceState = (languagePreferences: ClassLanguage[]) =>
  ({
    type: ClassLanguageActionTypes.ReplaceState,
    payload: languagePreferences,
  } as const);
